// Wariant Wise: lista pozycji listy zakupowej w modalu (nowy ux/ui + miniatura produktu).
// Logika oparta na pages/ShoppingLists/components/ShoppingListPositions z dodanym thumb.

import React, { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { Image as ImageIcon, Trash } from 'react-bootstrap-icons';

import {
  useGetShoppingListPositions,
  useDeleteShoppingListPosition,
  usePutShoppingListPosition,
  usePostShoppingListPositionQuantityDecrement,
  usePostShoppingListPositionQuantityIncrement,
  usePostShoppingListExport
} from 'api';
import { IShoppingListPositionListItem, IShoppingListPositionsRequest } from 'api/types';
import { Button, Counter, Modal } from 'components/controls';
import WiseSelect from 'components/wise/WiseSelect';
import { AddPositionsToCartSelect } from 'components/containers';

import productNoImage from 'assets/images/product-no-image.png';
import { FileExportIcon } from 'assets/icons';
import styles from 'theme/pages/wise/WiseShoppingLists/components/WiseShoppingListPositions/WiseShoppingListPositions.module.scss';

interface IProps {
  shoppingListId: number;
  refetchShoppingLists: () => void;
}

type IExportFileType = 'CSV' | 'XLS';

const ProductThumb: FC<{ url: string | null; alt: string }> = ({ url, alt }) => {
  const [errored, setErrored] = useState(false);
  const src = !url || errored ? productNoImage : url;
  return (
    <div className={styles.thumb}>
      <img src={src} alt={alt} onError={() => setErrored(true)} />
    </div>
  );
};

const WiseShoppingListPositions: FC<IProps> = ({ shoppingListId, refetchShoppingLists }) => {
  const { t } = useTranslation();

  const [queryParams, setQueryParams] = useState<IShoppingListPositionsRequest>({
    page: 1,
    limit: 20
  });
  const [checkedItemIds, setCheckedItemIds] = useState<number[]>([]);
  const [itemToDelete, setItemToDelete] = useState<IShoppingListPositionListItem | null>(null);
  const [updatingPositionIds, setUpdatingPositionIds] = useState<number[]>([]);
  const [exportFileType, setExportFileType] = useState<IExportFileType>('XLS');

  const {
    data: shoppingListPositions,
    refetch: refetchShoppingListPositions,
    isLoading
  } = useGetShoppingListPositions(shoppingListId, queryParams);

  const { mutate: deleteShoppingListPosition, isLoading: isRemoving } =
    useDeleteShoppingListPosition(shoppingListId, itemToDelete?.id || 0, {
      onSuccess: () => {
        setItemToDelete(null);
        refetchShoppingListPositions();
        refetchShoppingLists();
      }
    });

  const { mutate: exportShoppingList, isLoading: isExporting } = usePostShoppingListExport(
    shoppingListId,
    {
      onSuccess: (data) => {
        const a = document.createElement('a');
        a.download = data.file_name;
        a.href = `data:application/octet-stream;base64,${data.content}`;
        a.click();
      }
    }
  );

  const { mutate: updatePosition } = usePutShoppingListPosition(shoppingListId, {
    onSuccess: () => refetchShoppingListPositions(),
    onSettled: (data, error, params) => {
      setUpdatingPositionIds((prev) => prev.filter((id) => id !== params.positionId));
    }
  });

  const { mutate: increaseQuantity } = usePostShoppingListPositionQuantityIncrement(
    shoppingListId,
    {
      onSuccess: () => refetchShoppingListPositions(),
      onSettled: (data, error, params) => {
        setUpdatingPositionIds((prev) => prev.filter((id) => id !== params.positionId));
      }
    }
  );

  const { mutate: decreaseQuantity } = usePostShoppingListPositionQuantityDecrement(
    shoppingListId,
    {
      onSuccess: () => refetchShoppingListPositions(),
      onSettled: (data, error, params) => {
        setUpdatingPositionIds((prev) => prev.filter((id) => id !== params.positionId));
      }
    }
  );

  const handleChangeQuantity = (
    position: IShoppingListPositionListItem,
    newValue: number | null,
    isIncrement?: boolean
  ) => {
    setUpdatingPositionIds((prev) => [...prev, position.id]);
    if (newValue === null) {
      if (isIncrement) increaseQuantity({ positionId: position.id });
      else decreaseQuantity({ positionId: position.id });
    } else {
      updatePosition({
        positionId: position.id,
        quantity: newValue,
        unit_id: position.unit_id
      });
    }
  };

  const items = shoppingListPositions?.items ?? [];
  const allChecked = items.length > 0 && checkedItemIds.length === items.length;
  const someChecked = checkedItemIds.length > 0 && checkedItemIds.length < items.length;

  const toggleAll = () =>
    setCheckedItemIds(allChecked ? [] : items.map((item) => item.id));

  const toggleItem = (id: number) =>
    setCheckedItemIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const totalPages = shoppingListPositions?.total_pages || 1;
  const currentPage = queryParams.page || 1;

  return (
    <>
      <div
        className={classnames(
          styles.componentWrapper,
          'StylePath-Pages-Wise-WiseShoppingLists-Components-WiseShoppingListPositions'
        )}>
        {/* Pasek akcji */}
        <div className={styles.toolbar}>
          <div className={styles.toolbarLeft}>
            <AddPositionsToCartSelect
              positions={items
                .filter((item) => checkedItemIds.includes(item.id))
                .map((item) => ({
                  product_id: item.product_id,
                  quantity: item.quantity,
                  unit_id: item.unit_id
                }))}
              onSuccess={() => setCheckedItemIds([])}
            />
          </div>

          <div className={styles.toolbarRight}>
            <button
              type="button"
              className={styles.exportButton}
              onClick={() => exportShoppingList({ export_type: exportFileType })}
              disabled={isExporting}>
              <FileExportIcon />
              <Trans>Exportuj</Trans>
            </button>

            <div className={styles.exportFormat}>
              <WiseSelect<{ file_type: IExportFileType }>
                onChange={(item) => {
                  if (!Array.isArray(item) && item) {
                    setExportFileType(item.file_type);
                  }
                }}
                options={[
                  { value: 'XLS', label: 'XLS', item: { file_type: 'XLS' } },
                  { value: 'CSV', label: 'CSV', item: { file_type: 'CSV' } }
                ]}
                value={exportFileType}
              />
            </div>
          </div>
        </div>

        {/* Tabela */}
        <div className={styles.tableWrapper}>
          {isLoading ? (
            <div className={styles.skeletonList}>
              {[...Array(4)].map((_, i) => (
                <div key={i} className={styles.skeletonRow}>
                  <div className={styles.skeletonThumb} />
                  <div className={styles.skeletonLines}>
                    <div className={styles.skeletonLine} style={{ width: '70%' }} />
                    <div
                      className={styles.skeletonLine}
                      style={{ width: '40%', height: 10, marginTop: 6 }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className={styles.emptyState}>
              <ImageIcon />
              <span>
                <Trans>Lista zakupowa nie ma produktów</Trans>
              </span>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.colCheckbox}>
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={(el) => {
                        if (el) el.indeterminate = someChecked;
                      }}
                      onChange={toggleAll}
                    />
                  </th>
                  <th className={styles.colLp}>
                    <Trans>LP</Trans>
                  </th>
                  <th className={styles.colProduct}>
                    <Trans>Produkt</Trans>
                  </th>
                  <th className={styles.colQty}>
                    <Trans>Ilość</Trans>
                  </th>
                  <th className={styles.colUnit}>
                    <Trans>Jednostka</Trans>
                  </th>
                  <th className={styles.colRight}>
                    <Trans>Cena netto</Trans>
                  </th>
                  <th className={styles.colRight}>
                    <Trans>Cena brutto</Trans>
                  </th>
                  <th className={styles.colRight}>
                    <Trans>Wartość</Trans>
                  </th>
                  <th className={styles.colDelete} />
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const isChecked = checkedItemIds.includes(item.id);
                  const isUpdating = updatingPositionIds.includes(item.id);
                  return (
                    <tr key={item.id} className={classnames({ [styles.rowChecked]: isChecked })}>
                      <td className={styles.colCheckbox}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleItem(item.id)}
                        />
                      </td>
                      <td className={styles.colLp}>{(currentPage - 1) * 20 + index + 1}</td>
                      <td className={styles.colProduct}>
                        <div className={styles.productCell}>
                          <ProductThumb url={item.images?.[0]?.thumb ?? null} alt={item.name} />
                          <div className={styles.productInfo}>
                            <a href={`/${item.url_link}`} className={styles.productName}>
                              {item.name}
                            </a>
                            {item.index && (
                              <span className={styles.productSymbol}>{item.index}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className={styles.colQty}>
                        <div className={styles.counterWrap}>
                          <Counter
                            onChange={(value) => handleChangeQuantity(item, value)}
                            onIncrease={() => handleChangeQuantity(item, null, true)}
                            onDecrease={() => handleChangeQuantity(item, null, false)}
                            value={item.quantity}
                            disabled={isUpdating}
                          />
                        </div>
                      </td>
                      <td className={styles.colUnit}>{item.unit_name}</td>
                      <td className={styles.colRight}>
                        <span className={styles.priceCell}>
                          {item.price_net_formatted} {item.currency}
                        </span>
                      </td>
                      <td className={styles.colRight}>
                        <span className={styles.priceCell}>
                          {item.price_gross_formatted} {item.currency}
                        </span>
                      </td>
                      <td className={styles.colRight}>
                        <div className={styles.totalCell}>
                          <strong>
                            {item.total_price_gross_formatted} {item.currency}
                          </strong>
                          <span className={styles.totalMeta}>
                            <Trans>brutto</Trans>
                          </span>
                          <span className={styles.totalNet}>
                            {item.total_price_net_formatted} {item.currency}{' '}
                            <Trans>netto</Trans>
                          </span>
                        </div>
                      </td>
                      <td className={styles.colDelete}>
                        <button
                          type="button"
                          className={styles.deleteButton}
                          onClick={() => setItemToDelete(item)}
                          aria-label={t('Usuń') || 'delete'}>
                          <Trash />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!isLoading && totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              type="button"
              className={styles.pageBtn}
              disabled={currentPage <= 1}
              onClick={() =>
                setQueryParams((prev) => ({ ...prev, page: (prev.page || 1) - 1 }))
              }>
              <Trans>Poprzednia</Trans>
            </button>
            <span className={styles.pageInfo}>
              <Trans>Strona</Trans> <strong>{currentPage}</strong> / {totalPages}
            </span>
            <button
              type="button"
              className={styles.pageBtn}
              disabled={currentPage >= totalPages}
              onClick={() =>
                setQueryParams((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))
              }>
              <Trans>Następna</Trans>
            </button>
          </div>
        )}
      </div>

      {itemToDelete && (
        <Modal title={t('Potwierdzenie usunięcia')} onClose={() => setItemToDelete(null)}>
          <div className={styles.confirmationModal}>
            <Trans>Czy na pewno chcesz usunąć produkt?</Trans>
            <div className={styles.confirmationActions}>
              <Button onClick={() => setItemToDelete(null)} color="danger" ghost>
                <Trans>Anuluj</Trans>
              </Button>
              <Button
                color="danger"
                loading={isRemoving}
                onClick={() => deleteShoppingListPosition()}>
                <Trans>Usuń</Trans>
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default WiseShoppingListPositions;
